import type { FinalDecision } from '@/types';

const APPROVING: readonly FinalDecision[] = ['APPROVED', 'APPROVED_WITH_ALERTS'];

export interface GatePrerequisite {
  stage: string;
  label: string;
  approved: boolean;
  review_state: string | null;
  approved_at: string | null;
  approved_by: string | null;
}

export interface GatePrerequisiteState {
  satisfied: boolean;
  prerequisites: GatePrerequisite[];
}

/**
 * Resolve whether the gate sequence blocks this decision.
 *
 * The final gate carries the binding decision that activates the contract, and
 * it only means something if Gate 1 and Gate 2 were signed first. `outstanding`
 * drives the explanatory checklist; `blocked` disables submit.
 *
 * Only approving decisions are blocked, matching the server: a reviewer must
 * always be able to reject, and escalating is how they pass on a contract they
 * cannot approve. `review_gate_guard.py` enforces the same rule — this only
 * saves the reviewer filling in a form that would 409.
 *
 * A null state (the prerequisites call failed, or the caller has not loaded it)
 * blocks nothing. The server is the enforcement point, so failing open here
 * costs a round-trip and a clear error rather than a wrong activation.
 */
export function resolveGateSequence(
  state: GatePrerequisiteState | null,
  effectiveDecision: FinalDecision | null,
): { outstanding: GatePrerequisite[]; blocked: boolean } {
  const outstanding = state ? state.prerequisites.filter((p) => !p.approved) : [];
  return {
    outstanding,
    blocked:
      outstanding.length > 0 &&
      effectiveDecision !== null &&
      APPROVING.includes(effectiveDecision),
  };
}
